import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import {
  Component,
  ChangeDetectionStrategy,
  ComponentRef,
  OnInit,
} from '@angular/core';
import { Observable, switchMap, of, finalize, shareReplay, map } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

import { DprProgressGridData } from '@dpr-details/models/grid';
import {
  DprDetailsService,
  DprProgressGridService,
} from '@dpr-details/services';
import { DprDetailsPageBaseComponent } from '@dpr-details/models/component';
import { getDefaultSubMenuItems } from '@dpr-details/utils';
import { DprDetailsSync, DprProgress } from '@shared/models/dpr';
import { DprProgressGridComponent } from '@dpr-details/components';
import { DprProgressGridSync } from '@dpr-details/models/sync';
import { DprAttachmentsGridData, Sync } from '@shared/models/grid';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { HttpErrorResponse } from '@angular/common/http';

import { StaticMessage } from '@shared/enums';
import { getStaticMessage } from '@shared/utils';
import { ScreenSizeService, ToastNotificationService } from '@shared/services';
import { DprAttachmentService } from '@shared/services/dpr';
import { NavigationMenuItem } from '@shared/models';

@UntilDestroy()
@Component({
  selector: 'app-dpr-progress-page',
  templateUrl: './dpr-progress-page.component.html',
  styleUrls: ['./dpr-progress-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprProgressPageComponent
  extends DprDetailsPageBaseComponent<DprProgressGridData>
  implements OnInit
{
  labeling = {
    gridEntityName: $localize`:@@dprDetails.dprProgress.txtProgressRegistrations:Progress`,
  };

  StaticMessage = StaticMessage;

  preselectedItem: string | number;
  navigationSelectItems: NavigationMenuItem[] = getDefaultSubMenuItems();

  constructor(
    private dprProgressGridService: DprProgressGridService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprDetailsService: DprDetailsService,
    private toastNotificationService: ToastNotificationService,
    protected activatedRoute: ActivatedRoute,
    protected dprAttachmentService: DprAttachmentService,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute, dprAttachmentService);
  }

  private get dprProgress(): DprProgress {
    return this.dprDetails.dprProgress;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.preselectedItem =
      this.activatedRoute?.firstChild?.snapshot?.url[0]?.path;
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    if (
      this.dprProgressGridService.shouldAutoFill(this.registrationsGridData)
    ) {
      this.autoFillEmptyItems();
    }

    this.storeComponentChanges(this.activatedComponent);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges(this.attachmentsChanges);

    const saveChanges$ = validateChanges$.pipe(
      untilDestroyed(this),
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(
          this.registrationsChanges,
          this.attachmentsChanges,
          this.comment,
          isAutoSyncFromAdminChange
        );
      }),
      finalize(() => (this.isSaving = false)),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
  }

  //#region Protected methods

  protected isRegistrationComponent(component: ComponentRef<any>): boolean {
    return component instanceof DprProgressGridComponent;
  }

  protected setupData(): void {
    this.registrationsGridData = this.dprProgressGridService.buildGridData(
      this.dprDetails.dprProgress.dprProgressDetails,
      this.selectedSubproject.data.progress.progressItems
    );

    this.attachmentsGridData = this.dprAttachmentService.mapToGridData(
      this.dprProgress?.dprProgressAttachments
    );

    this.comment = this.dprProgress.comment;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected getOfflineRegistrationsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): Sync<DprProgressGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprProgress?.syncDprProgressDetails;

    if (!offlineChanges) {
      return null;
    }

    const offlineGridData = this.dprProgressGridService.buildGridData(
      [
        ...(offlineChanges.addedList ?? []),
        ...(offlineChanges.updatedList ?? []),
      ],
      this.selectedSubproject.data.progress.progressItems
    );

    return this.getOfflineGridDataChanges(offlineGridData, offlineChanges);
  }

  protected getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): Sync<DprAttachmentsGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprProgress?.syncDprProgressAttachments;

    return this.getOfflineAttachmentGridDataChanges(offlineChanges);
  }

  //#endregion

  //#region Private methods

  private validateChanges(
    attachmentsChanges: Sync<DprAttachmentsGridData>
  ): Observable<boolean> {
    if (!this.dprAttachmentService.validateChanges(attachmentsChanges)) {
      return of(false);
    }

    return of(true);
  }

  private saveChanges(
    registrationChanges: Sync<DprProgressGridData>,
    attachmentsChanges: Sync<DprAttachmentsGridData>,
    comment: string,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprProgress> {
    const syncDprProgressDetails = this.dprProgressGridService.mapToGridSync(
      registrationChanges,
      this.dprProgress.id
    );

    const syncData: DprProgressGridSync =
      this.dprDetailsSyncModelBuilderService.forDprProgress(
        this.selectedSubproject.id,
        this.dprDetails.id,
        this.dprProgress.id,
        syncDprProgressDetails,
        attachmentsChanges,
        comment
      );

    const saveChanges$ = this.dprDetailsService
      .syncDprProgress(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.progressTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprProgress): void {
    this.refreshData();
    this.offlineFacade.setDprProgress(result, this.dprDetails.id);
  }

  private autoFillEmptyItems(): void {
    if (!this.registrationsGridData?.length) {
      return;
    }

    this.toastNotificationService.info(
      getStaticMessage(
        StaticMessage.AutoFillText,
        this.customTabNames.progressTabName
      ),
      false,
      getStaticMessage(
        StaticMessage.AutoFillTitle,
        this.customTabNames.progressTabName
      )
    );

    this.registrationsGridData.forEach((x) => {
      if (x.clearedToday == null) {
        this.registrationsGridApi.editCell(x, 'clearedToday', 0);
      }
    });

    this.registrationsChanges = this.registrationsGridApi.getChanges();
  }

  //#endregion
}
